import React from 'react';
import { clsx } from 'clsx';
import { makeStyles } from '@mui/styles';

import { CreditRisk } from '@types';
import { moneyFormatter } from '@utils';
import { TableCropRow } from '@network';


interface Props {
  risk: CreditRisk;
  title?: string;
  className?: string;
  twoColors?: boolean;
}

export const RiskCrops = ({ risk, title, twoColors, className }: Props) => {
  const cls = useStyles();
  const { tableCrop } = risk;

  if (!tableCrop || !tableCrop.headers || !tableCrop.headers.length) {
    return null;
  }

  const rows = tableCrop.rows;
  let incomeTotal = 0;
  let profit = 0;

  for (const row of rows) {
    incomeTotal += +(row.incomeTotal || 0);
    profit += +(row.profit || 0);
  }

  const accumulatedRow: TableCropRow = {
    year: 'Accumulated',
    incomeTotal,
    profit,
  };

  const getValue = (value: number | string | null): string => {
    if (!value) {
      return '';
    } else {
      return typeof value === 'number' ? moneyFormatter.format(value || 0).toString() : value;
    }
  };

  return (
    <div className={clsx(className, 'risk-crops-wg')}>
      <div className={clsx(cls.title, 'title')}>{title ? title : 'Fiscal Income of Crop to Finance'}</div>
      <div className={cls.wrap}>
        <div className={clsx(cls.row, cls.categories, 'row')}>
          <div className={clsx(cls.category, cls.col, cls.col_full)}>Income</div>
        </div>
        <div className={clsx(cls.row, 'row')}>
          {tableCrop.headers.map(header => (
            <div key={header.key} className={clsx(cls.header, cls.col, cls.col_1)}>
              {header.label}
            </div>
          ))}
        </div>
        <div>
          {tableCrop.rows.map((row, idx) => (
            <div key={idx} className={clsx(cls.row, 'row')}>
              {tableCrop.headers.map((header) => (
                <div key={header.key} className={clsx(
                  cls.content, !twoColors && cls.bg, cls.col, cls.col_1,
                )}>
                  {getValue(row[header.key])}
                </div>
              ))}
            </div>
          ))}
          <div className={clsx(cls.row, 'row')}>
            {tableCrop.headers.map((header) => (
              <div key={header.key} className={clsx(cls.content, cls.col, cls.col_1)}>
                {getValue(accumulatedRow[header.key])}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  title: {
    margin: '25px 0 5px',
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--title-color)',
  },
  wrap: {
    border: '1px solid var(--primary-color)',
    borderRadius: 5,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
  },
  col: {
    backgroundColor: 'var(--background-color)',
  },
  col_1: {
    flex: '0 0 33.33%',
    maxWidth: '33.33%',
  },
  col_full: {
    flex: '0 0 100%',
    maxWidth: '100%',
  },
  categories: {
    backgroundColor: 'var(--primary-color)',
  },
  category: {
    minHeight: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--background-color)',
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: 'var(--primary-color)',
    borderLeft: '1px solid var(--background-color)',
    '&:first-child': {
      borderLeft: 'none',
    },
  },
  header: {
    minHeight: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--primary-color)',
    borderLeft: '1px solid var(--primary-color)',
    '&:first-child': {
      borderLeft: 'none',
    },
  },
  bg: {
    backgroundColor: 'var(--foreground-color)',
  },
  content: {
    borderTop: '1px solid var(--primary-color)',
    borderLeft: '1px solid var(--primary-color)',
    '&:first-child': {
      borderLeft: 'none',
    },
    minHeight: 34,
    fontSize: 12,
    fontWeight: 400,
    color: 'var(--primary-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
